import { TranslationKey } from '../i18n/translationKeys';
import { useLanguageStore } from '../stores/languageStore';

// Amounts are always CAD, so only their presentation follows the interface language. The bare
// language tag is not enough: 'en' renders "CA$1,234.56" where 'en-CA' renders "$1,234.56", which is
// the symbol a Canadian reader expects — hence the mapping to a regional locale.
const CURRENCY_LOCALES: Record<string, string> = {
    fr: 'fr-CA',
    en: 'en-CA',
};

// The language defaults to the store, so a caller that only wants to show an amount need not read it;
// callers holding one already pass it, and it may be i18next's current language, carrying a region
// ('en-US'). An unknown tag falls back to French rather than reaching Intl, which throws on one.
const currencyLocale = (locale: string): string =>
    CURRENCY_LOCALES[locale.slice(0, 2).toLowerCase()] ?? CURRENCY_LOCALES.fr;

export const formatCurrency = (value: number | null | undefined, locale: string = useLanguageStore.getState().lang) =>
    value !== null && value !== undefined
        ? new Intl.NumberFormat(currencyLocale(locale), { style: 'currency', currency: 'CAD' }).format(value)
        : '—';

const LANGUAGE_DISPLAY_MAP: Record<string, string> = {
    EN: 'ENG',
    FR: 'FR',
};

export const formatLanguageDisplay = (language: string | null | undefined): string | null => {
    if (!language) return null;
    return LANGUAGE_DISPLAY_MAP[language] ?? language;
};

// A custom snooze/reactivation date is a calendar date the user picked, stored at UTC midnight
// (see TaskService.AsUtc). Formatting it in the browser's local zone would shift it to the
// previous day west of UTC, so anchor the display to UTC to show the day that was selected.
export const formatSnoozeDate = (isoDate: string): string =>
    new Date(isoDate).toLocaleDateString(undefined, { timeZone: 'UTC' });

// Format a UTC-stored deactivatedAt timestamp as a UTC short date (day/month, UTC-anchored).
// Uses UTC to prevent negative-offset day rollback, matching formatSnoozeDate (see AB#293563).
export const formatDeactivatedAt = (isoDate: string, locale: string): string => {
    const date = new Date(isoDate);
    const day = date.getUTCDate();
    const month = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' })
        .format(date)
        .replace(/\.$/, '');
    return `${day} ${month}`;
};

// Building an Intl formatter is the costly part, and the forecast week formats every day on each render.
const dateFormatCache = new Map<string, Intl.DateTimeFormat>();
const cachedDateFormat = (key: string, locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
    const cacheKey = `${key}|${locale}`;
    let format = dateFormatCache.get(cacheKey);
    if (!format) {
        format = new Intl.DateTimeFormat(locale, options);
        dateFormatCache.set(cacheKey, format);
    }
    return format;
};
const formatUtcWeekdayDate = (isoDate: string, locale: string, weekday: 'short' | 'long'): string => {
    const label = cachedDateFormat(weekday, locale, { weekday, day: 'numeric', month: 'short', timeZone: 'UTC' })
        .format(new Date(isoDate))
        .replace(/\./g, '');
    return label.charAt(0).toUpperCase() + label.slice(1);
};

// A UTC-anchored day with its abbreviated weekday ("Mar 4 oct", "Tue, Oct 4"), for a list spanning the coming days.
export const formatWeekdayDate = (isoDate: string, locale: string): string => formatUtcWeekdayDate(isoDate, locale, 'short');

// A UTC-anchored day with its full weekday ("Mercredi 14 oct", "Wednesday, Oct 14"), for a day heading.
export const formatFullWeekdayDate = (isoDate: string, locale: string): string => formatUtcWeekdayDate(isoDate, locale, 'long');

// Day + short month in the viewer's local zone (e.g. "15 mai"), no time. Used by the client
// follow-up timeline, which shows the send day of each reminder without a timestamp.
export const formatShortDate = (isoDate: string, locale: string): string => {
    const date = new Date(isoDate);
    const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date).replace(/\.$/, '');
    return `${date.getDate()} ${month}`;
};

export const formatHistoryDate = (isoDate: string, locale: string): string => {
    const date = new Date(isoDate);
    const now = new Date();
    const day = date.getDate();
    const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date).replace(/\.$/, '');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    if (date.getFullYear() !== now.getFullYear()) {
        return `${day} ${month} ${date.getFullYear()} ${hh}:${mm}`;
    }
    return `${day} ${month} ${hh}:${mm}`;
};

// A translation key plus its interpolation values. Returned instead of a literal so the formatter
// stays free of user-facing copy — the caller translates.
export interface RelativeTimeLabel {
    key: string;
    options?: Record<string, unknown>;
}

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const isPreviousCalendarDay = (date: Date, now: Date): boolean => {
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return date.getFullYear() === yesterday.getFullYear()
        && date.getMonth() === yesterday.getMonth()
        && date.getDate() === yesterday.getDate();
};

/**
 * How long ago `date` was, as a translation key: "à l'instant", "il y a X min", "il y a X h",
 * "hier", then a short date. Elapsed time wins below 24 h so a sync from 23 h ago reads
 * "il y a 23 h" rather than the vaguer "hier"; the calendar-day check only decides what happens
 * past that. `now` is injectable so the buckets are testable without faking the clock.
 */
export const formatRelativeTime = (date: Date, locale: string, now: Date = new Date()): RelativeTimeLabel => {
    // A clock skew (or a server timestamp a few seconds ahead) must not render "in -1 min".
    const elapsed = Math.max(0, now.getTime() - date.getTime());

    if (elapsed < MINUTE_MS) return { key: TranslationKey.Home.LastSync.JustNow };
    if (elapsed < HOUR_MS) return { key: TranslationKey.Home.LastSync.Minutes, options: { count: Math.floor(elapsed / MINUTE_MS) } };
    if (elapsed < DAY_MS) return { key: TranslationKey.Home.LastSync.Hours, options: { count: Math.floor(elapsed / HOUR_MS) } };
    if (isPreviousCalendarDay(date, now)) return { key: TranslationKey.Home.LastSync.Yesterday };

    return { key: TranslationKey.Home.LastSync.OnDate, options: { date: formatHistoryDate(date.toISOString(), locale) } };
};

/**
 * Elapsed time for the sync checklist's per-phase stopwatch — "8 sec" below a minute, "1m 23s" at
 * or above it. A translation key rather than a literal string since the unit letters differ by
 * locale in general (kept identical here only because fr/en happen to agree on this short form).
 */
export const formatElapsedDuration = (totalSeconds: number): RelativeTimeLabel => {
    if (totalSeconds < 60) {
        return { key: TranslationKey.Home.SyncChecklist.ElapsedSeconds, options: { count: totalSeconds } };
    }
    return {
        key: TranslationKey.Home.SyncChecklist.ElapsedMinutes,
        options: { minutes: Math.floor(totalSeconds / 60), seconds: totalSeconds % 60 },
    };
};
